import concat from 'lodash/concat';
import pick from 'lodash/pick';
import { upsertById } from 'reducers/utils';

import {
  CLEAR_ADD_SERVICE_ERROR,
  CLEAR_SELECTION,
  CREATE_SERVICE_FAIL,
  CREATE_SERVICE_START,
  FETCH_SERVICES_FAIL,
  FETCH_SERVICES_START,
  FETCH_SERVICES_SUCCESS,
  SERVICE_RESET_SEARCH_GROUPS,
  SERVICE_SEARCH_GROUPS_FAIL,
  SERVICE_SEARCH_GROUPS_START,
  SERVICE_SEARCH_GROUPS_SUCCESS,
  SERVICE_SET_SELECTED_GROUPS,
  SET_ADD_SERVICE_ERROR,
  TOGGLE_SERVICE_SELECTED,
  UPDATE_SERVICE_IN_SERVICES_LIST,
  SERVICE_UPDATE_FILTER,
} from 'actions/services';
import {
  ADD_GRAPH_EDGE_START,
  DELETE_EDGE_FROM_REDUX,
  FETCH_GRAPH_NODES_START,
  FETCH_GRAPH_NODES_FAIL,
  FETCH_GRAPH_NODES_SUCCESS,
  FETCH_GRAPH_EDGES_FAIL,
  FETCH_GRAPH_EDGES_START,
  FETCH_GRAPH_EDGES_SUCCESS,
} from 'actions/services/dependenciesGraph';
import { ProcessStatus } from '@xmatters/common/constants/commonStatus';
import keyBy from 'lodash/keyBy';
import {
  addEdgeToNodes,
  addNewEdge,
  deleteEdge,
  deleteEdgesFromNodes,
  updateNodeEdgeLinks,
} from './util';

export const initialState = {
  count: 0,
  total: 0,
  data: [],
  loading: false,
  error: false,
  loaded: false,
  addServiceError: null,
  groups: {
    count: 0,
    total: 0,
    data: [],
    loading: false,
    error: false,
  },
  dependenciesGraph: {
    graph: {
      nodes: {},
      edges: {},
    },
    fetchStatus: {
      dependencies: ProcessStatus.NOT_STARTED,
      services: ProcessStatus.NOT_STARTED,
    },
    lastSuccessfulFetchTimestamp: { dependencies: 0, services: 0 },
  },
};

export default function services(state = initialState, action) {
  switch (action.type) {
    case FETCH_SERVICES_START:
      return {
        ...state,
        loading: true,
        loaded: false,
        error: false,
        ...pick(action, ['sortBy', 'sortOrder']),
      };
    case FETCH_SERVICES_SUCCESS:
      return {
        ...state,
        ...action.payload,
        data: action.replaceExisting
          ? action.payload.data
          : concat(state.data, action.payload.data),
        loading: false,
        loaded: true,
        error: false,
      };
    case FETCH_SERVICES_FAIL:
      return {
        ...state,
        loading: false,
        loaded: true,
        error: true,
      };
    case SERVICE_UPDATE_FILTER:
      return {
        ...state,
        loaded: false,
      };
    case UPDATE_SERVICE_IN_SERVICES_LIST: {
      const { dependenciesGraph, data: existingData } = state;
      const { graph } = dependenciesGraph;
      const { service } = action;
      const { id } = service;
      const graphNode = {
        id,
        sourceEdges: [],
        targetEdges: [],
        ...graph.nodes[id],
        ...service,
      };
      const data = upsertById(existingData, id, service);

      return {
        ...state,
        data,
        dependenciesGraph: {
          ...dependenciesGraph,
          graph: {
            ...graph,
            nodes: {
              ...graph.nodes,
              [id]: graphNode,
            },
          },
        },
      };
    }
    case TOGGLE_SERVICE_SELECTED:
      return {
        ...state,
        data: state.data.map(s =>
          s.id === action.id ? { ...s, selected: !s.selected } : s,
        ),
      };
    case CLEAR_SELECTION:
      return {
        ...state,
        data: state.data.map(s => ({
          ...s,
          selected: false,
        })),
      };
    case CLEAR_ADD_SERVICE_ERROR:
    case CREATE_SERVICE_START:
      return {
        ...state,
        addServiceError: null,
      };
    case CREATE_SERVICE_FAIL:
      return {
        ...state,
        addServiceError: action.payload.json.subcode,
      };
    case SET_ADD_SERVICE_ERROR:
      return {
        ...state,
        addServiceError: action.err,
      };
    case SERVICE_SEARCH_GROUPS_START:
      return {
        ...state,
        groups: {
          ...state.groups,
          loading: true,
          error: false,
        },
      };
    case SERVICE_SEARCH_GROUPS_SUCCESS:
      return {
        ...state,
        groups: {
          ...action.payload,
          loading: false,
          error: false,
        },
      };
    case SERVICE_SEARCH_GROUPS_FAIL:
      return {
        ...state,
        groups: {
          ...state.groups,
          loading: false,
          error: true,
        },
      };
    case SERVICE_RESET_SEARCH_GROUPS:
    case SERVICE_SET_SELECTED_GROUPS:
      return {
        ...state,
      };
    case FETCH_GRAPH_EDGES_START: {
      const { dependenciesGraph } = state;
      return {
        ...state,
        dependenciesGraph: {
          ...dependenciesGraph,
          fetchStatus: {
            ...dependenciesGraph.fetchStatus,
            dependencies: ProcessStatus.IN_PROGRESS,
          },
        },
      };
    }
    case ADD_GRAPH_EDGE_START: {
      const { graph } = state.dependenciesGraph;
      const { edge: newEdge } = action.payload;
      return {
        ...state,
        dependenciesGraph: {
          ...state.dependenciesGraph,
          graph: {
            ...graph,
            nodes: addEdgeToNodes(graph.nodes, newEdge),
            edges: addNewEdge(graph.edges, newEdge),
          },
        },
      };
    }

    case DELETE_EDGE_FROM_REDUX: {
      // When we delete an edge, we also update the client-side redux cache
      const { edgeId: deletedEdgeId } = action.payload;
      const { dependenciesGraph } = state;
      const { nodes, edges } = dependenciesGraph.graph;

      return {
        ...state,
        // Leaving fetchStatus to NOT_STARTED so spinners don't show up
        dependenciesGraph: {
          ...dependenciesGraph,
          graph: {
            // For now graph only contains nodes & edges so the next line is redundant; for future proof only
            ...dependenciesGraph.graph,
            nodes: deleteEdgesFromNodes(nodes, edges, deletedEdgeId),
            edges: deleteEdge(edges, deletedEdgeId),
          },
        },
      };
    }
    case FETCH_GRAPH_EDGES_SUCCESS: {
      const { dependenciesGraph } = state;
      const { graph } = dependenciesGraph;
      const edgesList = action.payload.data.map(
        ({ id, service, dependentService }) => ({
          // dependentService DEPENDS ON Service.
          // Arrow starts at dependent and points at service.
          source: dependentService.id,
          target: service.id,
          // This ID is used by us
          id,
          // This ID is used by jsPlumb. edge.getId() returns this value
          data: { id },
        }),
      );
      const nodes = updateNodeEdgeLinks(graph.nodes, edgesList);
      const edges = keyBy(edgesList, 'id');

      return {
        ...state,
        dependenciesGraph: {
          ...dependenciesGraph,
          graph: {
            ...dependenciesGraph.graph,
            edges,
            nodes,
          },
          fetchStatus: {
            ...dependenciesGraph.fetchStatus,
            dependencies: ProcessStatus.SUCCESS,
          },
          lastSuccessfulFetchTimestamp: {
            ...dependenciesGraph.lastSuccessfulFetchTimestamp,
            dependencies: Date.now(),
          },
        },
      };
    }
    case FETCH_GRAPH_EDGES_FAIL: {
      const { dependenciesGraph } = state;
      return {
        ...state,
        dependenciesGraph: {
          ...dependenciesGraph,
          fetchStatus: {
            ...dependenciesGraph.fetchStatus,
            dependencies: ProcessStatus.ERROR,
          },
          lastSuccessfulFetchTimestamp: {
            ...dependenciesGraph.lastSuccessfulFetchTimestamp,
            dependencies: 0,
          },
        },
      };
    }
    case FETCH_GRAPH_NODES_START: {
      const { dependenciesGraph } = state;

      return {
        ...state,
        dependenciesGraph: {
          ...dependenciesGraph,
          fetchStatus: {
            ...dependenciesGraph.fetchStatus,
            services: ProcessStatus.IN_PROGRESS,
          },
        },
      };
    }
    case FETCH_GRAPH_NODES_SUCCESS: {
      const { dependenciesGraph } = state;
      const { graph } = dependenciesGraph;
      const nodes = updateNodeEdgeLinks(action.payload.data, graph.edges);

      return {
        ...state,
        dependenciesGraph: {
          ...dependenciesGraph,
          graph: {
            ...dependenciesGraph.graph,
            nodes,
          },
          fetchStatus: {
            ...dependenciesGraph.fetchStatus,
            services: ProcessStatus.SUCCESS,
          },
          lastSuccessfulFetchTimestamp: {
            ...dependenciesGraph.lastSuccessfulFetchTimestamp,
            services: Date.now(),
          },
        },
      };
    }
    case FETCH_GRAPH_NODES_FAIL: {
      const { dependenciesGraph } = state;
      return {
        ...state,
        dependenciesGraph: {
          ...dependenciesGraph,
          fetchStatus: {
            ...dependenciesGraph.fetchStatus,
            services: ProcessStatus.ERROR,
          },
          lastSuccessfulFetchTimestamp: {
            ...dependenciesGraph.lastSuccessfulFetchTimestamp,
            services: 0,
          },
        },
      };
    }
    default:
      return state;
  }
}
